import { useRef, useEffect } from "react"
import StackEdit from "stackedit-js"

interface StackEditEditorProps {
    open: boolean
    onClose: () => void
    initialContent?: string
    onSave?: (content: string) => void
    onCloseEditor?: (content: string) => void
    title?: string
}

const StackEditEditor = ({
    open,
    onClose,
    initialContent = "",
    onSave,
    onCloseEditor,
    title = "Edit Content",
}: StackEditEditorProps) => {
    const stackEditRef = useRef<StackEdit | null>(null)
    const onSaveRef = useRef(onSave)
    const onCloseRef = useRef(onClose)
    const onCloseEditorRef = useRef(onCloseEditor)
    const isInitialFileChangeRef = useRef(true)
    const latestContentRef = useRef<string>("")
    const wasOpenedRef = useRef(false)

    // Keep refs updated
    useEffect(() => {
        onSaveRef.current = onSave
        onCloseRef.current = onClose
        onCloseEditorRef.current = onCloseEditor
    }, [onSave, onClose, onCloseEditor])

    // Initialize StackEdit once on mount
    useEffect(() => {
        if (!stackEditRef.current) {
            try {
                stackEditRef.current = new StackEdit()

                stackEditRef.current.on("fileChange", (file: { content: { text: string } }) => {
                    if (isInitialFileChangeRef.current) {
                        isInitialFileChangeRef.current = false
                        latestContentRef.current = file.content.text
                        return
                    }

                    latestContentRef.current = file.content.text
                })

                try {
                    stackEditRef.current.on("close", () => {
                        const contentToSave = latestContentRef.current || ""

                        if (contentToSave && onSaveRef.current) {
                            onSaveRef.current(contentToSave)
                        }

                        if (contentToSave && onCloseEditorRef.current && wasOpenedRef.current) {
                            onCloseEditorRef.current(contentToSave)
                        }

                        wasOpenedRef.current = false

                        if (onCloseRef.current) {
                            onCloseRef.current()
                        }
                    })
                } catch (error) {}
            } catch (error) {
                console.error("StackEditEditor: Error initializing StackEdit", error)
            }
        }

        return () => {
            if (stackEditRef.current) {
                try {
                    stackEditRef.current.close()
                } catch (error) {
                    console.error("StackEditEditor: Error closing StackEdit during cleanup", error)
                }
                stackEditRef.current = null
            }
        }
    }, [])

    // Open/close StackEdit based on open prop
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null

        if (open && stackEditRef.current) {
            isInitialFileChangeRef.current = true
            latestContentRef.current = initialContent

            timeoutId = setTimeout(() => {
                if (stackEditRef.current) {
                    try {
                        wasOpenedRef.current = true
                        stackEditRef.current.openFile({
                            name: title || "Document",
                            content: {
                                text: initialContent,
                            },
                        })
                    } catch (error) {
                        console.error("StackEditEditor: Error opening StackEdit", error)
                        wasOpenedRef.current = false
                    }
                }
            }, 100)
        } else if (!open && stackEditRef.current && wasOpenedRef.current) {
            try {
                const contentToSave = latestContentRef.current || initialContent

                if (contentToSave && onSaveRef.current) {
                    onSaveRef.current(contentToSave)
                }
                if (contentToSave && onCloseEditorRef.current && wasOpenedRef.current) {
                    onCloseEditorRef.current(contentToSave)
                } else {
                }

                try {
                    stackEditRef.current.close()
                } catch (closeError) {
                    console.error("StackEditEditor: StackEdit was already closed or not open")
                }

                wasOpenedRef.current = false
            } catch (error) {
                console.error("StackEditEditor: Error closing StackEdit", error)
                wasOpenedRef.current = false
            }
        } else if (!open) {
            wasOpenedRef.current = false
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
        }
    }, [open, initialContent, title])

    return null
}

export default StackEditEditor
